import { useMemo } from "react";
import { useWatch } from "react-hook-form";

import { QuestionResponse, QuestionnaireGetResponse } from "capitalrx-types";

import { getQuestionStatus, QuestionPathStatus } from "@/utils";

type Args = {
  questionnaire: QuestionnaireGetResponse,
  sortedQuestions: QuestionResponse[],
}

export const useQuestionnaireDetermination = ({ questionnaire, sortedQuestions }: Args) => {

  const allQuestionIds = useMemo(
    () => sortedQuestions.map((question) => `${questionnaire.id}_${question.question_id}`),
    [questionnaire, sortedQuestions],
  );

  const allFormValues = useWatch({ name: allQuestionIds });
  const formJson = JSON.stringify(allFormValues);

  const value = useMemo(() => {
    // these must be ready before handleResponse will work
    if (!sortedQuestions) {
      return {};
    }

    const answers: { [key: string]: string } = Object.fromEntries(
      sortedQuestions.map((question, index) => {
        // include incoming answer which is set to formdata async
        const answer = allFormValues[index];
        return [question.question_id, answer];
      }),
    );

    const areAllQuestionsFreeform = sortedQuestions.every((q) => q.question_type == "freeform");

    if (areAllQuestionsFreeform) {
      const newQuestionStatus: { [key: string]: QuestionPathStatus } = Object.fromEntries(
        sortedQuestions.map((question) => [question.question_id, "in_path"]),
      );

      return {
        activeQuestions: sortedQuestions,
        questionStatus: newQuestionStatus,
      };

    } else {
      const [newQuestionStatus, newQuestionsInPath] = getQuestionStatus(sortedQuestions, answers);

      /* find where to split active from remaining
         -- UX assumes next question index is always greater than the previous question index, which is the case
            with existing questionnaires, but is not enforced.
        */
      let lastActiveIndex = 0;
      // start at last in_path question
      if (newQuestionsInPath.length) {
        const inPathIndexes = newQuestionsInPath.map((question) => sortedQuestions.indexOf(question as QuestionResponse));
        lastActiveIndex = Math.max(...inPathIndexes);
      }

      // then find first unanswered applicable question
      while (lastActiveIndex < sortedQuestions.length) {
        const question = sortedQuestions[lastActiveIndex];
        const status = newQuestionStatus[question.question_id];
        const answer = answers[question.question_id];

        if (status != "not_applicable" && !answer) {
          break;
        }
        lastActiveIndex++;
      }

      lastActiveIndex = Math.min(lastActiveIndex, sortedQuestions.length - 1);

      // then include not applicable questions that immediately follow
      while (lastActiveIndex < sortedQuestions.length) {
        const question = sortedQuestions[lastActiveIndex];
        const status = newQuestionStatus[question.question_id];
        if (status != "not_applicable") {
          break;
        }
        lastActiveIndex++;
      }
      lastActiveIndex = Math.min(lastActiveIndex, sortedQuestions.length - 1);

      return {
        activeQuestions: sortedQuestions.slice(0, lastActiveIndex + 1),
        questionStatus: newQuestionStatus,
      };
    }
    /* TODO: Need to find a stable reference that changes on form values changing.
     Using form values JSON for now */

  }, [formJson, sortedQuestions]);

  return value;
};
